/**
 * The Feedback inbox's "Errors (auto-logged)" view (8 Oct).
 *
 * Rusty: "anytime this happens log a feedback". Every failure a person hit —
 * the API's own 5xx, Ivanti refusing something, a screen that crashed — is
 * logged by the server as a feedback item; this lists them for IT, one row per
 * error and endpoint over the last 7 days: how often, how many people, where,
 * Ivanti's reason (scrubbed server-side) and the last reference a person was
 * shown. Unlike the assistant review, people are named: this is IT's triage
 * screen, and the person is who IT will follow up with.
 *
 * `?ref=` (the Teams card's link) narrows it to the error a person quoted.
 *
 * 9 Oct: open groups by default; each can be resolved with a fix note for
 * everyone who hit it, and reopens by itself ("came back") when the same
 * error happens again. Count and reference fold into the other columns so the
 * table fits without scrolling sideways.
 *
 * 12 Oct: one-screen layout — the view chips, status chips and counts share
 * one row, each cell is one or two lines, and a click on the reason opens the
 * full reason, route, reference and every user under the row.
 */

import { Badge, Button, DataGrid, type DataGridColumn, Typography } from "@norican/react";
import { type ReactNode, useMemo } from "react";

import { EmptyStateBlock, ErrorState, LoadingRows } from "@/components/common";
import { useT } from "@/i18n";
import type { AdminErrorGroup } from "@/lib/api";

import { useAdminErrorFeedback } from "./api";
import {
  Clamp,
  COMPACT_GRID_CLASS,
  DetailField,
  DetailPanel,
  ShortDateTime,
} from "./compact-cells";
import {
  EmptyForStatus,
  ResolutionFixCell,
  ResolutionStatusCell,
  StatusFilterChips,
} from "./resolution-controls";
import type { StatusFilter } from "./resolution-helpers";

export interface AdminErrorFeedbackProps {
  /** The inbox's view chips, first on this view's filter row. */
  toolbar?: ReactNode;
  /** The reference to narrow to, or `null` for every error. */
  refFilter: string | null;
  /** Clears `refFilter`. */
  onShowAll: () => void;
  /** Open / Resolved / All. */
  status: StatusFilter;
  onStatusChange: (next: StatusFilter) => void;
}

/** The whole error group, full length: opens under the row on click. */
function ErrorDetail({ row }: { row: AdminErrorGroup }) {
  const { t } = useT("assistant");
  return (
    <DetailPanel>
      <DetailField label={t("feedbackAdmin.errors.column.reason")}>{row.reason || "–"}</DetailField>
      <DetailField label={t("feedbackAdmin.errors.column.where")}>
        {[row.screen || "-", row.endpoint].join("\n")}
        {"\n"}
        {[row.code, row.status > 0 ? `${row.status} · ${row.source}` : row.source].join(" · ")}
        {"\n"}
        {t("feedbackAdmin.errors.column.ref")} <span className="font-mono">{row.last_ref}</span>
      </DetailField>
      <DetailField label={`${t("feedbackAdmin.errors.column.users")} (${row.users_affected})`}>
        {(row.users ?? []).join("\n") || "–"}
      </DetailField>
    </DetailPanel>
  );
}

export function AdminErrorFeedback({
  toolbar,
  refFilter,
  onShowAll,
  status,
  onStatusChange,
}: AdminErrorFeedbackProps) {
  const { t } = useT("assistant");
  // A Teams card's `?ref=` link always finds its error, resolved or not.
  const shown: StatusFilter = refFilter === null ? status : "all";
  const list = useAdminErrorFeedback({ ref: refFilter, status: shown });

  const columns = useMemo<DataGridColumn<AdminErrorGroup>[]>(() => {
    const detail = (row: AdminErrorGroup) => <ErrorDetail row={row} />;
    return [
      {
        id: "lastSeen",
        header: t("feedbackAdmin.errors.column.lastSeen"),
        width: 130,
        cell: (row) => (
          <span className="flex items-center gap-1">
            <ShortDateTime value={row.last_seen} />
            <Badge tone={row.count > 1 ? "danger" : "neutral"} variant="subtle" size="sm">
              {t("feedbackAdmin.errors.times", { count: row.count })}
            </Badge>
          </span>
        ),
      },
      {
        id: "error",
        header: t("feedbackAdmin.errors.column.error"),
        width: 180,
        cell: (row) => (
          <span className="flex min-w-0 flex-col" title={`${row.code} · ${row.last_ref}`}>
            <span className="truncate">{row.code}</span>
            <Typography variant="caption" tone="muted" className="truncate">
              {row.status > 0 ? `${row.status} · ${row.source}` : row.source}
            </Typography>
          </span>
        ),
      },
      {
        id: "where",
        header: t("feedbackAdmin.errors.column.where"),
        width: 190,
        cell: (row) => (
          <span className="flex min-w-0 flex-col" title={row.endpoint}>
            <span className="truncate">{row.screen || "-"}</span>
            <Typography variant="caption" tone="muted" className="truncate">
              {row.endpoint}
            </Typography>
          </span>
        ),
      },
      {
        id: "reason",
        header: t("feedbackAdmin.errors.column.reason"),
        width: 300,
        cell: (row) => (row.reason ? <Clamp>{row.reason}</Clamp> : null),
        renderCellDetail: detail,
      },
      {
        id: "users",
        header: t("feedbackAdmin.errors.column.users"),
        width: 170,
        cell: (row) => (
          <span className="flex min-w-0 flex-col" title={(row.users ?? []).join("\n")}>
            <span>{row.users_affected}</span>
            {(row.users ?? []).length === 0 ? null : (
              <Typography variant="caption" tone="muted" className="truncate">
                {(row.users ?? [])[0]}
                {(row.users ?? []).length > 1 ? ` +${(row.users ?? []).length - 1}` : ""}
              </Typography>
            )}
          </span>
        ),
      },
      {
        id: "status",
        header: t("feedbackAdmin.resolution.column.status"),
        width: 70,
        align: "center",
        cell: (row) => <ResolutionStatusCell resolution={row.resolution} />,
      },
      {
        id: "fix",
        header: t("feedbackAdmin.resolution.column.fix"),
        width: 170,
        cell: (row) => (
          <ResolutionFixCell
            kind="error"
            itemKey={row.group}
            resolution={row.resolution}
            subject={`${row.code} · ${row.screen || row.endpoint}`}
          />
        ),
      },
    ];
  }, [t]);

  const items = list.data?.items ?? [];
  const total = items.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {toolbar}
        <StatusFilterChips
          id="errors-status-filter"
          value={shown}
          counts={list.data?.counts}
          onChange={onStatusChange}
        />
        {refFilter === null ? null : (
          <span className="flex items-center gap-2">
            <Badge tone="neutral" variant="outline">
              {t("feedbackAdmin.errors.refFilter", { ref: refFilter })}
            </Badge>
            <Button variant="ghost" tone="neutral" size="sm" onClick={onShowAll}>
              {t("feedbackAdmin.errors.showAll")}
            </Button>
          </span>
        )}
        {items.length === 0 ? null : (
          <Typography variant="caption" tone="muted">
            {shown === "open"
              ? t("feedbackAdmin.errors.summaryOpen", { count: total, distinct: items.length })
              : t("feedbackAdmin.errors.summary", { count: total, distinct: items.length })}
          </Typography>
        )}
      </div>
      <Typography variant="caption" tone="muted">
        {t("feedbackAdmin.errors.subtitle")}
      </Typography>
      {list.isLoading ? (
        <LoadingRows rows={5} />
      ) : list.error != null ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyForStatus status={shown} counts={list.data?.counts}>
          <EmptyStateBlock
            title={t("feedbackAdmin.errors.empty.title")}
            description={t("feedbackAdmin.errors.empty.hint")}
          />
        </EmptyForStatus>
      ) : (
        <DataGrid
          aria-label={t("feedbackAdmin.errors.tableLabel")}
          columns={columns}
          data={items}
          getRowId={(row) => row.group}
          tableLayout="fixed"
          density="compact"
          className={COMPACT_GRID_CLASS}
        />
      )}
    </div>
  );
}
